<script setup>
import { nextTick, ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import T from './CompanyProfileJobsTab.i18n.js';
import ShowJob from 'JIX/jobad/elements/ElementsManager.js';

const store = useStore();

const intersectOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 0,
};

const intersectionObserver = new IntersectionObserver(handleIntersect, intersectOptions);

const companyName = computed(() => store.state.companyName);
const jobsContent = computed(() => store.state.jobsContent);

const jobs = ref();

const showJob = ref(null);
const page = ref(10);
const loading = ref(false);

onMounted(() => {
    setupObserver();
    showJob.value = new ShowJob({});
});

onUnmounted(() => {
    intersectionObserver.value.disconnect();
});

watch(
    () => jobsContent.value.jobsContentHtml,
    () => {
        nextTick(() => {
            setupObserver();
        });
    },
    { deep: true }
);

function setupObserver() {
    if (jobsContent.value.jobsContentHtml.length > 0 && jobsContent.value.noJobs > page.value) {
        const elem = jobs.value.at(-1);
        intersectionObserver.observe(elem);
    }
}

async function handleIntersect(entries, observer) {
    if (entries.some(x => x.isIntersecting)) {
        entries.forEach(x => observer.unobserve(x.target));

        loading.value = true;
        await store.dispatch('loadJobs', { page: page.value  });
        loading.value = false;
        page.value += 10;
    }
}
</script>

<template>
    <div>
        <div class="row">
            <div class="col-md-8">
                <h3> {{ T.header(companyName) }} </h3>
            </div>
        </div>

        <div class="row">
            <div :class="jobsContent.unsolicitedUrl ? 'col-md-8' : 'col-md-12'">
                <div v-if="jobsContent.jobsContentHtml.length === 0">
                    {{ T.noJobsIntro(companyName) }}
                    <div class="mb-3 bg-surface-default border mt-2">
                        <div class="row p-3">
                            <div :class="jobsContent.unsolicitedUrl ? 'col-md-9' : 'col-md-10'">
                                <div>
                                    <strong> {{ T.jobagentHeader(companyName) }} </strong>
                                </div>
                                {{ T.jobagentShort(companyName) }}
                            </div>
                            <div class="col-md-2 p-3">
                                <button
                                    class="btn btn-sm btn-primary btn--create-agent js-popup-agent"
                                    type="button"
                                    data-jobagent-source="jobsearch_top"
                                    data-jobagent-tag="add_jobsearch"
                                >
                                    {{ T.jobagentButton() }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else>
                    <div
                        v-for="job in jobsContent.jobsContentHtml"
                        :key="job"
                        ref="jobs"
                    >
                        <div v-html="job" />
                    </div>
                    <div class="my-5 text-center" v-if="loading">
                        <div class="spinner-border text-info" role="status">
                            <span class="sr-only">{{ T.loadingJobs() }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div
                class="col-md-4"
                v-if="jobsContent.unsolicitedUrl"
            >
                <div class="w-100 h-10 p-3 text-sm mb-3 bg-surface-default border">
                    <div>
                        <strong> {{ T.unsolicitedShortText() }} </strong>
                    </div>
                    {{ T.unsolicitedLongerText() }}
                    <a :href="jobsContent.unsolicitedUrl">
                        <button class="mt-2 btn btn-primary btn-sm w-100">
                            {{ T.unsolicitedShortText() }}
                        </button>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>
